import {
  accessCodes,
  searchContextItems,
  type SearchFunctionMap,
  type SearchResult
} from '@gat-ship/types'
import { IconLink } from '@gat-ship/icons'
import type { ModalStack } from '@gat-ship/vue-components'
import type { UserService } from '@gat-ship/user'
import { filterNavigationItems } from './navigation-search-filter'

/**
 * Built-in, permission-gated navigation shortcuts surfaced by the
 * global and navigation search contexts. Matches case-insensitively on
 * title and `searchValues` aliases. Selecting one opens the matching
 * AdministrationModal panel via the central modal stack
 * (`?modals=admin:<panel>`) — the `/admin/*` route views were retired in
 * central-modal-host Phase 7.
 */
export function getNavigationSearchResults(
  search: string | undefined,
  user: UserService,
  stack: ModalStack
): SearchResult[] {
  if (!search) {
    return []
  }
  const result: SearchResult[] = []
  const icon = IconLink
  const openAdminPanel = (panel: string) => () => stack.open('admin', { panel })
  if (user.isTenantAdmin.value) {
    result.push({
      id: 'navigation.tenant-users',
      title: 'Tenant Users',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Tenant Users',
      searchValues: ['Tenant', 'Tenants', 'administration'],
      icon,
      actions: {
        selected: openAdminPanel('tenant-users')
      }
    })
    result.push({
      id: 'navigation.access-roles-list',
      title: 'Access Roles',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Access Roles',
      searchValues: ['Access', 'Accesses', 'administration'],
      icon,
      actions: {
        selected: openAdminPanel('access-roles')
      }
    })
    result.push({
      id: 'navigation.companies',
      title: 'Companies',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Companies',
      searchValues: ['Company', 'Companies', 'administration'],
      icon,
      actions: {
        selected: openAdminPanel('companies')
      }
    })
    result.push({
      id: 'navigation.currency-codes',
      title: 'Currencies',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Currencies',
      searchValues: ['Currency', 'Currencies', 'administration'],
      icon,
      actions: {
        selected: openAdminPanel('currencies')
      }
    })
  }
  if (user.hasPermission(accessCodes.system.agency.viewAgencyStructure)) {
    result.push({
      id: 'navigation.agency-structure',
      title: 'Agency Structure',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Agency Structure',
      searchValues: ['Agency', 'Agencies', 'administration'],
      icon,
      actions: {
        selected: openAdminPanel('agency-structure')
      }
    })
  }
  return filterNavigationItems(result, search)
}

/**
 * The built-in providers backing the global fan-out and the
 * `navigation` context, closed over the signed-in user for permission
 * gating and the modal stack for opening admin panels.
 */
export function createBuiltInSearchFunctions(
  user: UserService,
  stack: ModalStack
): SearchFunctionMap {
  return {
    global: async (search) => getNavigationSearchResults(search, user, stack),
    navigation: async (search) =>
      getNavigationSearchResults(search, user, stack)
  }
}
